import { getAuth, type User } from "firebase/auth";
import type { FirebaseApp } from "firebase/app";
import { isUnknownArray, isRecord } from "../types/data";
import type { ExperienceEventInput } from "../types/experience";
import type { PublicStats } from "../types/profile";
import { invalidateCachedValue, readCachedValue, writeCachedValue } from "./localDataCache";
import { getUserProfileCacheKey } from "./profileCache";
import {
  markActivityLogStale,
  markNotificationsLogStale
} from "./activityFeedFreshness";

const LOCAL_SERVER_PORT = process.env.REACT_APP_SHARE_SERVER_PORT || "3002";
const REQUEST_TIMEOUT_MS = 20000;
type SettlementUser = Pick<User, "uid" | "getIdToken">;
type SettlementAuth = { currentUser: SettlementUser | null };
type SettlementApp = FirebaseApp & { auth?: () => SettlementAuth };
export interface ExperienceSettlementOptions {
  user?: SettlementUser | null;
  auth?: SettlementAuth | null;
  firestore?: { app: SettlementApp } | null;
  db?: { app: FirebaseApp } | null;
  firebase?: { auth: () => SettlementAuth } | null;
  userId?: string;
  reason?: string;
}
export interface SettlementExperienceEvent extends ExperienceEventInput { key: string; experience: number }
export interface ExperienceSourceSummary {
  ratings?: number;
  ratingHistory?: number;
  components?: number;
  achievements?: number;
  challenges?: number;
  weeklyCards?: number;
  expeditions?: number;
  weeklyQuality?: number;
  weeklyStreak?: number;
}
export interface SuccessfulExperienceSettlement {
  applied: boolean;
  reason?: undefined;
  experienceDelta: number;
  experienceEvents: SettlementExperienceEvent[];
  totalExperience: number;
  publicStats: PublicStats;
  sourceSummary: ExperienceSourceSummary;
}
export interface UnauthenticatedExperienceSettlement {
  applied: false;
  reason: "auth-required";
  publicStats?: undefined;
  experienceDelta?: undefined;
  experienceEvents?: undefined;
  totalExperience?: undefined;
  sourceSummary?: undefined;
}
export type ExperienceSettlementResult = SuccessfulExperienceSettlement | UnauthenticatedExperienceSettlement;
interface PendingSettlement {
  trailingRequested: boolean;
  latest: { options: ExperienceSettlementOptions; authUser: SettlementUser };
  promise: Promise<SuccessfulExperienceSettlement> | null;
}
const pendingByUserId = new Map<string, PendingSettlement>();

const readPublicStats = (value: unknown): PublicStats | null => {
  if (!isRecord(value)) return null;
  const numericFields = ["watchedFilms", "watchedSeries", "countriesCount", "weeklyStreak", "level", "totalExperience", "averageRating"];
  if (numericFields.some((key) => value[key] !== undefined && typeof value[key] !== "number")) return null;
  if (value.favoriteGenre !== undefined && typeof value.favoriteGenre !== "string") return null;
  if (value.ogImageVersion !== undefined && typeof value.ogImageVersion !== "string" && typeof value.ogImageVersion !== "number") return null;
  return value;
};
const isSettlementEvent = (event: unknown): event is SettlementExperienceEvent => {
  if (!isRecord(event) || typeof event.key !== "string" || typeof event.experience !== "number") return false;
  const textFields = ["scope", "type", "category", "mode", "title", "description", "dedupeKey"];
  return textFields.every((key) => event[key] === undefined || typeof event[key] === "string") &&
    (event.metadata === undefined || isRecord(event.metadata));
};
const readSourceSummary = (value: unknown): ExperienceSourceSummary => {
  if (!isRecord(value)) return {};
  const fields = ["ratings", "ratingHistory", "components", "achievements", "challenges", "weeklyCards", "expeditions", "weeklyQuality", "weeklyStreak"];
  return fields.every((key) => value[key] === undefined || typeof value[key] === "number") ? value : {};
};

const getRequestUrls = (location: Partial<Pick<Location, "hostname" | "port" | "origin">> | null = typeof window !== "undefined" ? window.location : null) => {
  const configured = String(process.env.REACT_APP_SHARE_SERVER_URL || "").replace(/\/$/, "");
  const isLoopback = ["localhost", "127.0.0.1", "::1"].includes(
    String(location?.hostname || "").toLowerCase()
  );
  const isLocalReactServer = isLoopback && location?.port !== LOCAL_SERVER_PORT;
  const origin = configured || (isLocalReactServer
    ? `http://localhost:${LOCAL_SERVER_PORT}`
    : location?.origin || "");
  const paths = isLocalReactServer
    ? ["/api/experience/settle", "/share/experience/settle"]
    : ["/share/experience/settle", "/api/experience/settle"];
  return [...new Set(paths.map((path) => `${origin}${path}`))];
};

const resolveUser = ({ user, auth, firestore, db, firebase }: ExperienceSettlementOptions): SettlementUser | null => {
  if (user?.uid && typeof user.getIdToken === "function") return user;
  if (auth?.currentUser) return auth.currentUser;
  const compatUser = firestore?.app?.auth?.()?.currentUser ||
    firebase?.auth?.()?.currentUser;
  if (compatUser) return compatUser;
  const app = db?.app || firestore?.app;
  try {
    return app ? getAuth(app).currentUser : getAuth().currentUser;
  } catch {
    return null;
  }
};

const updateClientProfile = (userId: string, publicStats: PublicStats) => {
  if (!userId || !publicStats) return;
  const cacheKey = getUserProfileCacheKey(userId);
  const cached = readCachedValue<import("../types/profile").ProfileUser>(cacheKey, Number.POSITIVE_INFINITY);
  if (cached && typeof cached === "object") {
    writeCachedValue(cacheKey, {
      ...cached,
      publicStats: { ...(cached.publicStats || {}), ...publicStats }
    }, { preserveTimestamp: true });
  } else {
    void invalidateCachedValue(cacheKey);
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("movie-profile-experience-updated", {
      detail: { userId, publicStats, source: "trusted-server-settlement" }
    }));
  }
};

const fetchWithTimeout = async (url: string|URL|Request, options: RequestInit|undefined) => {
  if (typeof AbortController === "undefined") return fetch(url, options);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
};

const performExperienceSettlementRequest = async (
  options: ExperienceSettlementOptions, authUser: SettlementUser
): Promise<SuccessfulExperienceSettlement> => {
  const token = await authUser.getIdToken();
  const requestOptions = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    // Deliberately no amount, total, level, component reward, or delta.
    body: JSON.stringify({ reason: String(options.reason || "source-changed").slice(0, 80) })
  };
  let response: Response | null = null;
  let lastError: unknown = null;
  const urls = getRequestUrls();
  for (let index = 0; index < urls.length; index += 1) {
    try {
      response = await fetchWithTimeout(urls[index], requestOptions);
    } catch (error) {
      lastError = error;
      response = null;
      continue;
    }
    if (response.status === 404 && index < urls.length - 1) continue;
    break;
  }
  if (!response) {
    const error: Error & { code?: string; status?: number } = new Error("Trusted experience settlement is unavailable");
    error.code = "experience-settlement/network-error";
    error.cause = lastError;
    throw error;
  }
  const rawPayload: unknown = await response.json().catch(() => ({}));
  const payload = isRecord(rawPayload) ? rawPayload : {};
  if (!response.ok || payload?.ok === false) {
    const error: Error & { code?: string; status?: number } = new Error(typeof payload.error === "string" ? payload.error : "Trusted experience settlement failed");
    error.code = typeof payload.code === "string" ? payload.code : "experience-settlement/request-failed";
    error.status = response.status;
    throw error;
  }
  const publicStats = readPublicStats(payload.publicStats);
  if (!publicStats) {
    const error: Error & { code?: string; status?: number } = new Error("Trusted experience settlement returned no profile summary");
    error.code = "experience-settlement/invalid-response";
    throw error;
  }
  return {
    applied: Number(payload.experienceDelta) !== 0,
    experienceDelta: Number(payload.experienceDelta) || 0,
    experienceEvents: isUnknownArray(payload.experienceEvents)
      ? payload.experienceEvents.filter(isSettlementEvent)
      : [],
    totalExperience: Number(publicStats.totalExperience) || 0,
    publicStats,
    sourceSummary: readSourceSummary(payload.sourceSummary)
  };
};

export const requestExperienceSettlement = async (options: ExperienceSettlementOptions = {}): Promise<ExperienceSettlementResult> => {
  const authUser = resolveUser(options);
  const userId = String(options.userId || authUser?.uid || "").trim();
  if (!authUser || !userId || authUser.uid !== userId) {
    return { applied: false, reason: "auth-required" };
  }

  const pending = pendingByUserId.get(userId);
  if (pending) {
    // A second source can be persisted while the first settlement is still
    // reading its snapshot (for example, a rating completes a weekly card).
    // Returning the old promise used to discard that newer source change and
    // briefly publish an incomplete XP total. Coalesce bursts into one
    // mandatory trailing pass and let every caller observe its final result.
    pending.trailingRequested = true;
    pending.latest = { options, authUser };
    return pending.promise!;
  }

  const state: PendingSettlement = {
    trailingRequested: false,
    latest: { options, authUser },
    promise: null
  };
  const operation = (async () => {
    let result: SuccessfulExperienceSettlement | null = null;
    let lastError: unknown = null;
    let experienceDelta = 0;
    const experienceEventsByKey = new Map<string, SettlementExperienceEvent>();
    do {
      const current = state.latest;
      state.trailingRequested = false;
      try {
        result = await performExperienceSettlementRequest(
          current.options,
          current.authUser
        );
        experienceDelta += Number(result?.experienceDelta) || 0;
        (result?.experienceEvents || []).forEach((event) => {
          const key = String(event?.key || "").trim();
          if (key) experienceEventsByKey.set(key, event);
        });
        lastError = null;
      } catch (error) {
        lastError = error;
        if (!state.trailingRequested) throw error;
      }
    } while (state.trailingRequested);

    if (lastError) throw lastError;
    result = {
      ...result!,
      experienceDelta,
      applied: experienceDelta !== 0,
      experienceEvents: [...experienceEventsByKey.values()]
    };
    // Remove the quiet queue before notifying React. A synchronous listener
    // that persists another source must start a fresh pass instead of queuing
    // work behind a loop that has already finished.
    if (pendingByUserId.get(userId) === state) pendingByUserId.delete(userId);
    if (result?.publicStats) updateClientProfile(userId, result.publicStats);
    if (result?.experienceEvents?.length) {
      markNotificationsLogStale(userId, "owner");
      if (result.experienceEvents.some(
        (event) => ["weekly_streak", "weekly_card"].includes(String(event?.metadata?.sourceType))
      )) {
        markActivityLogStale(userId);
      }
    }
    return result;
  })();
  state.promise = operation;
  pendingByUserId.set(userId, state);
  try {
    return await operation;
  } finally {
    if (pendingByUserId.get(userId) === state) pendingByUserId.delete(userId);
  }
};
